import type { Channel, ScheduledMessage } from '@nexo/types';
import { IconButton, Popover, useToast } from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatDay, formatTime } from '../../lib/format';
import { errorMessage } from '../../lib/forms';
import { keys } from '../../lib/queries';

/** «N mensajes programados» above the composer, with a list to cancel them. */
export function ScheduledMessages({ channel, parentId }: { channel: Channel; parentId?: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: keys.scheduled(channel.id),
    queryFn: () => api.scheduled.list(channel.id),
    // They leave the list when sent.
    refetchInterval: 60_000,
  });
  const cancel = useMutation({
    mutationFn: api.scheduled.cancel,
    onSuccess: (_, id) =>
      queryClient.setQueryData<ScheduledMessage[]>(keys.scheduled(channel.id), (items) =>
        items?.filter((item) => item.id !== id),
      ),
    onError: (err) =>
      toast({ title: t('No se pudo cancelar'), description: errorMessage(err), tone: 'error' }),
  });

  const items = (list.data ?? []).filter((item) => item.parentId === (parentId ?? null));
  if (items.length === 0) return null;

  return (
    <Popover
      align="left"
      className="w-80 p-2"
      trigger={({ toggle }) => (
        <button
          type="button"
          onClick={toggle}
          className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-accent-soft px-2.5 py-1 text-xs font-medium text-accent hover:opacity-80"
        >
          <CalendarClock className="size-3.5" />
          {items.length === 1
            ? t('1 mensaje programado')
            : t('{{count}} mensajes programados', { count: items.length })}
        </button>
      )}
    >
      {() => (
        <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
          {items.map((item) => (
            <li key={item.id} className="flex items-start gap-2 rounded-lg p-2 hover:bg-surface-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-accent">
                  {formatDay(item.sendAt)} · {formatTime(item.sendAt)}
                </p>
                <p className="line-clamp-2 text-sm break-words">{item.body}</p>
              </div>
              <IconButton
                label={t('Cancelar envío')}
                size="sm"
                onClick={() => cancel.mutate(item.id)}
              >
                <X />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
    </Popover>
  );
}
